import { useQuery } from "@tanstack/react-query";
import { getDownloadUrl } from "../api/files.api";
import { getAdminExplorerDownloadUrl } from "../api/admin.api";
import { getTrashDownloadUrl } from "../api/trash.api";

/**
 * Fetches a presigned download URL for a file.
 * Cached for 14 minutes (just under the S3 presigned URL's 15-min TTL).
 * Shared across all components — re-opening the preview never re-fetches
 * until the URL is about to expire.
 * When `explorerUserId` is set, uses admin read-only explorer endpoints for that user's file.
 * When `trash` is set, uses trash endpoints (file is soft-deleted but not yet purged).
 */
export function useDownloadUrl(fileId: string, explorerUserId?: string, trash?: boolean) {
  return useQuery({
    queryKey: trash
      ? ["download-url", "trash", fileId]
      : explorerUserId
        ? ["download-url", "admin", explorerUserId, fileId]
        : ["download-url", fileId],
    // Forwarding the signal lets React Query actually cancel the in-flight
    // request (not just stop caring about its result) when fileId changes
    // again before it resolves — e.g. while rapidly paging through photos.
    queryFn: ({ signal }) =>
      trash
        ? getTrashDownloadUrl(fileId, signal)
        : explorerUserId
          ? getAdminExplorerDownloadUrl(explorerUserId, fileId, signal)
          : getDownloadUrl(fileId, signal),
    staleTime: 14 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}
